import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Search, Calendar, User, ChevronRight, ChevronDown, Inbox, Settings, Filter, Download, Plus, Trash2, X, Mail, Phone, MapPin, Building2, MessageSquare, Clock, FileText, CheckCircle2, AlertCircle, CalendarRange, Pencil } from 'lucide-react';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import Loader from '../../../components/Loader/Loader';
import { createColumnHelper } from '@tanstack/react-table';
import PremiumModuleWrapper from '../../Task/Components/common/PremiumModuleWrapper';
import { LeadDetails as CommonLeadDetails } from '../../../components/common/LeadDetails/LeadDetails';
import DynamicCalendar from '../../../components/DynamicCalendar/DynamicCalendar';
import SubHeader from '../../../components/common/SubHeader/SubHeader';
import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PipelineDealActionsOperation from './PipelineDealActionsOperation';
import usePermissions from '../../Master_Settings/usePermissions';
import './PipelineHistory.css';

const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;
const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
const NEW_ADDED_BASE_URL = (process.env.REACT_APP_BACKEND_BASEURL_FOR_DEVELOPMENT || '').replace(/\/$/, '');
const PIPELINE_HISTORY_BIND = `${NEW_BASE_URL}${API_ENDPOINTS.PIPELINE.PIPELINE_HISTORY_BIND}`;
const Custome_Search = `${NEW_BASE_URL}${API_ENDPOINTS.PIPELINE.GET_CUSTOME_SEARCH}`;

const getInitials = (name) => {
    if (!name) return '??';
    const trimmed = name.trim();
    
    // Logic for phone numbers or numbers: get first two digits (skipping + if present)
    const digitMatch = trimmed.match(/^\+?(\d{2})/);
    if (digitMatch && digitMatch[1]) {
        return digitMatch[1];
    }

    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '??';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const generateColor = (name) => {
    if (!name) return 'hsl(210, 60%, 60%)';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    return `hsl(${h}, 70%, 60%)`;
};

const getAvatarStyle = (name) => {
    const baseColor = generateColor(name || 'Unknown');
    return {
        bg: baseColor.replace('60%)', '92%)'),
        text: baseColor.replace('60%)', '45%)'),
        border: baseColor.replace('60%)', '80%)')
    };
};

const getPipelineBadgeClass = (name) => {
    if (!name) return 'pb-default';
    const n = name.toLowerCase();
    if (n.includes('solar')) return 'pb-solar';
    if (n.includes('study')) return 'pb-study';
    if (n.includes('sales')) return 'pb-sales';
    return 'pb-default';
};

const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === '-' || dateStr === 'null' || dateStr === 'undefined' || dateStr === '0') return 'N/A';
    
    try {
        const monthNames = { 'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11 };
        const cleanStr = String(dateStr).trim();
        let dateObj = null;

        // 1. Handle WCF Date format: /Date(1741151000000)/
        if (cleanStr.includes('/Date(')) {
            const timestampStr = cleanStr.match(/\/Date\((\d+)\)\//);
            if (timestampStr && timestampStr[1]) {
                dateObj = new Date(parseInt(timestampStr[1], 10));
            }
        }

        // 2. Try standard constructor
        if (!dateObj) {
            const d = new Date(cleanStr);
            if (!isNaN(d.getTime())) dateObj = d;
        }

        // 3. Robust Manual Parsing for various common Kit19 formats
        if (!dateObj) {
            const parts = cleanStr.split(/[\s\-\/]+/);
            if (parts.length >= 3) {
                let day, month, year;
                // Scenario: MMM DD YYYY (e.g. Mar 5 2026)
                if (monthNames[parts[0]] !== undefined) {
                    month = monthNames[parts[0]];
                    day = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                } 
                // Scenario: DD MMM YYYY (e.g. 05 Mar 2026)
                else if (monthNames[parts[1]] !== undefined) {
                    day = parseInt(parts[0], 10);
                    month = monthNames[parts[1]];
                    year = parseInt(parts[2], 10);
                }
                // Scenario: YYYY MM DD
                else if (parts[0].length === 4) {
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10) - 1;
                    day = parseInt(parts[2], 10);
                }

                if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                    dateObj = new Date(year, month, day);
                }
            }
        }

        if (!dateObj || isNaN(dateObj.getTime())) return dateStr;
        
        const d = String(dateObj.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const m = months[dateObj.getMonth()];
        const y = dateObj.getFullYear();
        
        return `${d}-${m}-${y}`;
    } catch (e) {
        return dateStr;
    }
};

const getNormalizedDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === '-' || dateStr === 'null' || dateStr === 'undefined' || dateStr === '0') return null;
    
    try {
        const monthNames = { 'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11 };
        const cleanStr = String(dateStr).trim();
        let dateObj = null;

        if (cleanStr.includes('/Date(')) {
            const timestampStr = cleanStr.match(/\/Date\((\d+)\)\//);
            if (timestampStr && timestampStr[1]) {
                dateObj = new Date(parseInt(timestampStr[1], 10));
            }
        }

        if (!dateObj) {
            const d = new Date(cleanStr);
            if (!isNaN(d.getTime())) dateObj = d;
        }

        if (!dateObj) {
            const parts = cleanStr.split(/[\s\-\/]+/);
            if (parts.length >= 3) {
                let day, month, year;
                // Scenario: MMM DD YYYY (e.g. Mar 5 2026)
                if (monthNames[parts[0]] !== undefined) {
                    month = monthNames[parts[0]];
                    day = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                } 
                // Scenario: DD MMM YYYY (e.g. 05 Mar 2026)
                else if (monthNames[parts[1]] !== undefined) {
                    day = parseInt(parts[0], 10);
                    month = monthNames[parts[1]];
                    year = parseInt(parts[2], 10);
                }
                // Scenario: YYYY MM DD
                else if (parts[0].length === 4) {
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10) - 1;
                    day = parseInt(parts[2], 10);
                }

                if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                    dateObj = new Date(year, month, day);
                }
            }
        }

        if (!dateObj || isNaN(dateObj.getTime())) return null;
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`; // Returns YYYY-MM-DD in local time
    } catch (e) {
        return null;
    }
};

const getInitialLast30DaysRange = () => {
    const today = new Date();
    const last30 = new Date(today);
    last30.setDate(today.getDate() - 30);

    const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
    };

    return {
        from: fmt(last30),
        to: fmt(today),
        label: 'Last 30 Days'
    };
};

const PipelineHistory = () => {
    const { permissions } = usePermissions('PAG10307');
    const historyPerms = useMemo(() => {
        if (!permissions || !Array.isArray(permissions)) return null;
        return permissions.find(p => p.ModuleCode?.includes('PAG10307')) || {};
    }, [permissions]);

    // --- State ---
    const [viewMode, setViewMode] = useState('list'); // 'grid', 'list', 'calendar'
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilters, setStatusFilters] = useState([]); // New state for card filtering

    // Date Range State
    const [dateRange, setDateRange] = useState(getInitialLast30DaysRange());
    const [tempCustomRange, setTempCustomRange] = useState({ from: getInitialLast30DaysRange().from, to: getInitialLast30DaysRange().to });
    const [activeRangeLabel, setActiveRangeLabel] = useState(getInitialLast30DaysRange().label);
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    // Filter State
    const [filterCount, setFilterCount] = useState(0);
    const [advanceFilterData, setAdvanceFilterData] = useState(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [viewingLeadId, setViewingLeadId] = useState(null);
    const [calendarView, setCalendarView] = useState('month');
    const [sorting, setSorting] = useState([{ id: 'CreatedDate', desc: true }]); // Default sort
    const [editingDeal, setEditingDeal] = useState(null); // Deal being edited

    // --- Map Data for Calendar ---
    const getValidDate = (dateStr) => {
        if (!dateStr || dateStr === 'N/A' || dateStr === '0' || dateStr === 'null') return null;
        try {
            const monthNames = { 'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5, 'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11 };
            const cleanStr = String(dateStr).trim().toLowerCase();
            
            // 1. Handle WCF Date format: /Date(1741151000000)/
            if (cleanStr.includes('/date(')) {
                const match = cleanStr.match(/\d+/);
                if (match) return new Date(parseInt(match[0]));
            }

            // 2. Try native parsing (handles ISODate, some MMM DD YYYY etc.)
            let d = new Date(cleanStr);
            if (d && !isNaN(d.getTime())) return d;

            // 3. Robust Manual Parsing for various Kit19 formats
            const parts = cleanStr.split(/[\s\-\/\,]+/);
            if (parts.length >= 3) {
                let day, month, year;
                
                // Scenario: MMM DD YYYY or MMM DD, YYYY
                if (monthNames[parts[0]] !== undefined) {
                    month = monthNames[parts[0]];
                    day = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                } 
                // Scenario: DD MMM YYYY
                else if (monthNames[parts[1]] !== undefined) {
                    day = parseInt(parts[0], 10);
                    month = monthNames[parts[1]];
                    year = parseInt(parts[2], 10);
                }
                // Scenario: YYYY MM DD
                else if (parts[0].length === 4) {
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10) - 1;
                    day = parseInt(parts[2], 10);
                }
                // Scenario: DD MM YYYY
                else {
                    day = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10) - 1;
                    year = parseInt(parts[2], 10);
                }

                if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                    if (year < 100) year += 2000;
                    return new Date(year, month, day);
                }
            }
            return null;
        } catch (e) { return null; }
    };

    const getStatusByProb = (prob) => {
        const p = parseInt(prob || '0');
        if (p >= 100) return 'won';
        if (p >= 76) return 'hot';
        if (p >= 51) return 'warm';
        if (p >= 26) return 'cool';
        return 'lost';
    };

    const mappedEvents = useMemo(() => {
        const events = historyData.map(item => {
            // Check EstimatedClosureDate first, then fallback to CreatedDate
            const validDate = getValidDate(item.EstimatedClosureDate) || getValidDate(item.CreatedDate);
            return {
                id: item.pipemapid || item.LeadId || Math.random(),
                title: item.PipeLineName || 'Untitled Deal',
                date: validDate, 
                 // Map to keys matching statusConfig in DynamicCalendar
                computedStatus: getStatusByProb(item.Probability),
                status: item.PipeLineStage,
                probability: item.Probability,
                amount: item.Amount,
                leadName: item.PersonName,
                owner: item.DealOwnerName,
                original: item
            };
        }).filter(ev => ev.date !== null);
        console.log("CALENDAR DEBUG:", events.length, "items mapped out of", historyData.length);
        return events;
    }, [historyData]);

    const renderCalendarEvent = (event, view) => {
        const prob = parseInt(event.probability || '0');
        let borderColor = '#ef4444'; 
        if (prob >= 100) borderColor = '#10b981';
        else if (prob >= 76) borderColor = '#3b82f6';
        else if (prob >= 51) borderColor = '#6366f1';
        else if (prob >= 26) borderColor = '#f59e0b';

        if (view === 'year') return null;

        const isMonthView = view === 'month';

        return (
            <div 
                className={`ph-calendar-card ${isMonthView ? 'compact' : ''}`}
                style={{ borderLeft: `3px solid ${borderColor}` }}
            >
                <div className="ph-card-header">
                    <span className={`ph-card-badge ${getProbabilityBadgeClass(event.probability)}`}>
                        {event.status}
                    </span>
                    {!isMonthView && (
                        <span className="ph-card-amount">₹{parseFloat(event.amount || '0').toLocaleString()}</span>
                    )}
                </div>
                <div className="ph-card-title" title={event.title}>{event.title}</div>
                
                <div className="ph-card-footer">
                    <div 
                        className="ph-card-user ph-clickable-lead-area" 
                        onClick={(e) => {
                            e.stopPropagation();
                            const leadId = event.original.LeadId || event.original.LeadID || event.original.PID || event.original.EnquiryId;
                            if (leadId) setViewingLeadId(leadId);
                        }}
                    >
                        <div className="ph-mini-initials" style={{ 
                            backgroundColor: getAvatarStyle(event.leadName).bg,
                            color: getAvatarStyle(event.leadName).text,
                            borderColor: getAvatarStyle(event.leadName).border,
                            width: isMonthView ? '16px' : '24px',
                            height: isMonthView ? '16px' : '24px',
                            fontSize: isMonthView ? '8px' : '11px'
                        }}>
                            {getInitials(event.leadName)}
                        </div>
                        <span className="truncate ph-lead-text-link">{event.leadName}</span>
                    </div>
                    {!isMonthView && (
                        <div className="ph-card-owner">
                            <User size={10} className="text-gray-400" />
                            <span className="truncate">{event.owner}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Predefined Ranges
    const getPredefinedRange = (type) => {
        const today = new Date();
        const formatDate = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        let from, to, label;
        to = formatDate(today);

        switch (type) {
            case 'Today':
                from = to;
                label = 'Today';
                break;
            case 'Yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                from = formatDate(yesterday);
                to = from;
                label = 'Yesterday';
                break;
            case 'Last 7 Days':
                const last7 = new Date(today);
                last7.setDate(today.getDate() - 7);
                from = formatDate(last7);
                label = 'Last 7 Days';
                break;
            case 'Last 30 Days':
                const last30 = new Date(today);
                last30.setDate(today.getDate() - 30);
                from = formatDate(last30);
                label = 'Last 30 Days';
                break;
            case 'This Month':
                from = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
                label = 'This Month';
                break;
            case 'Last Month':
                const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                from = formatDate(firstDayLastMonth);
                to = formatDate(lastDayLastMonth);
                label = 'Last Month';
                break;
            case 'Last Year':
                from = formatDate(new Date(today.getFullYear() - 1, 0, 1));
                to = formatDate(new Date(today.getFullYear() - 1, 11, 31));
                label = 'Last Year';
                break;
            default:
                from = to;
                label = 'Today';
        }
        return { from, to, label };
    };

    const handleRangeSelect = (type) => {
        setActiveRangeLabel(type);
        if (type === 'Custom Range') {
            setTempCustomRange({ from: dateRange.from, to: dateRange.to });
            // Don't close, let them pick dates
            return;
        }
        const range = getPredefinedRange(type);
        setDateRange(range);
        setDatePickerOpen(false);
    };

    const handleCustomDateChange = (field, value) => {
        setTempCustomRange(prev => ({ ...prev, [field]: value }));
    };

    const applyCustomRange = () => {
        setDateRange({
            from: tempCustomRange.from,
            to: tempCustomRange.to,
            label: 'Custom Range'
        });
        setDatePickerOpen(false);
    };

    // Custom Search State
    const [customSearchOptions, setCustomSearchOptions] = useState([]);
    const [selectedCustomSearch, setSelectedCustomSearch] = useState(null);
    const [customSearchDropdownOpen, setCustomSearchDropdownOpen] = useState(false);
    const [customSearchDropdownSearch, setCustomSearchDropdownSearch] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // --- Fetch Custom Search Options ---
    const fetchCustomSearch = async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const payload = {
                Token: TOKEN,
                Details: {
                    userId: parseInt(uid)
                }
            };
            console.log("Fetching custom search with payload:", payload);

            const response = await fetch(Custome_Search, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const result = await response.json();
            console.log("Custom Search API Response:", result);

            const data = result?.Details || result?.d || [];
            setCustomSearchOptions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching custom search:", error);
            setCustomSearchOptions([]);
        }
    };


    // --- Fetch History Data ---
    const fetchHistory = async () => {
        setLoading(true);
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const payload = {
                Token: TOKEN,
                LoggedUserId: uid,
                Message: "",
                MAC_Address: "",
                IP_Address: "",
                Details: {
                    userId: uid,
                    PipelineName: "",
                    EstFromDt: "",
                    EstToDt: "",
                    CreateFromDt: dateRange.from,
                    CreateToDt: dateRange.to,
                    LeadName: "",
                    CriteriaName: selectedCustomSearch ? selectedCustomSearch.CriteriaName : "",
                    Remarks: "",
                    SortBy: "desc",
                    ColumnName: "pipemapid"
                }
            };

            const response = await fetch(PIPELINE_HISTORY_BIND, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const result = await response.json();
            const data = result?.Details || result?.d || [];
            setHistoryData(Array.isArray(data) ? data : []);
            setCurrentPage(1); // Reset to first page on new data
        } catch (error) {
            console.error("Error fetching pipeline history:", error);
            setHistoryData([]);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchCustomSearch();
    }, []);

    useEffect(() => {
        fetchHistory();
    }, [dateRange, selectedCustomSearch]);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, advanceFilterData, selectedCustomSearch, dateRange, statusFilters, sorting]);

    // Handle Summary Card Clicks
    const handleStatusFilter = (key) => {
        if (key === 'ResetAll') {
            setSearchTerm('');
            setAdvanceFilterData(null);
            setFilterCount(0);
            setStatusFilters([]);
            setSelectedCustomSearch(null);
            return;
        }

        if (key === 'All' || key === 'total' || key === 'totalValue') {
            setStatusFilters([]);
            return;
        }
        // Toggle filter
        setStatusFilters(prev => 
            prev.includes(key) ? prev.filter(f => f !== key) : [key] 
        );
    };

    // --- Advanced Filter Logic ---
    const handleAdvanceFilterSubmit = (filterData) => {
        setAdvanceFilterData(filterData);
        if (filterData && filterData.rows) {
            setFilterCount(filterData.rows.length);
        } else {
            setFilterCount(0);
        }
    };

    const advanceFilterFields = [
        { label: 'Pipeline Name', value: 'PipeLineName' },
        { label: 'Estimated Closure Date', value: 'EstimatedClosureDate', type: 'date' },
        { label: 'Created Date', value: 'CreatedDate', type: 'date' },
        { label: 'Remarks', value: 'Remarks' },
        { label: 'Lead Name', value: 'PersonName' },
        { label: 'Owner Name', value: 'DealOwnerName' },
        { label: 'Is Deleted (1 for Yes)', value: 'Isdeleted' },
        { label: 'Deleted On', value: 'DeletedOn', type: 'date' }
    ];

    // --- Export Logic ---
    const handleExport = () => {
        if (filteredData.length === 0) {
            alert("No data available to export.");
            return;
        }

        const exportData = filteredData.map(item => ({
            "Pipeline Name": item.PipeLineName || '',
            "Amount": parseFloat(item.Amount || 0).toFixed(2),
            "Owner": item.DealOwnerName || '',
            "Probability (%)": item.Probability || '0',
            "Stage": item.PipeLineStage || '',
            "Closure Date": formatDate(item.EstimatedClosureDate),
            "Created Date": formatDate(item.CreatedDate),
            "Created By": `${item.FName || ''} ${item.LName || ''}`.trim() || item.DealOwnerName || '',
            "Lead Name": item.PersonName || '',
            "Is Deleted": item.Isdeleted === "1" ? "Yes" : "No",
            "Deleted On": formatDate(item.DeletedOn)
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Pipeline History");
        
        // Generate filename with current date
        const dateStr = new Date().toLocaleDateString().replace(/\//g, '-');
        XLSX.writeFile(workbook, `PipelineHistory_${dateStr}.xlsx`);
    };

    // --- Filter Results ---
    const filteredData = useMemo(() => {
        let data = historyData;

        // Apply card status filters
        if (statusFilters.length > 0) {
            data = data.filter(item => {
                const isDeleted = String(item.Isdeleted) === "1";
                if (statusFilters.includes('active')) return !isDeleted;
                if (statusFilters.includes('deleted')) return isDeleted;
                return true;
            });
        }

        // Apply simple search
        if (searchTerm) {
            const lowTerm = searchTerm.toLowerCase();
            data = data.filter(item =>
                (item.PipeLineName || '').toLowerCase().includes(lowTerm) ||
                (item.PersonName || '').toLowerCase().includes(lowTerm) ||
                (item.PipeLineStage || '').toLowerCase().includes(lowTerm)
            );
        }

        // Apply advanced filters (from SubHeader)
        if (advanceFilterData && advanceFilterData.rows && advanceFilterData.rows.length > 0) {
            const { logic, rows } = advanceFilterData;
            
            data = data.filter(item => {
                const matches = rows.map(filter => {
                    if (!filter.value) return true;
                    
                    const fieldConfig = advanceFilterFields.find(f => f.value === filter.field);
                    const isDateField = fieldConfig?.type === 'date';

                    if (isDateField) {
                        const normalizedItemVal = getNormalizedDate(item[filter.field]);
                        const normalizedFilterVal = filter.value; // SubHeader date input is already YYYY-MM-DD

                        if (!normalizedItemVal) return false;

                        switch (filter.operator) {
                            case 'equals': return normalizedItemVal === normalizedFilterVal;
                            case 'not_equal': return normalizedItemVal !== normalizedFilterVal;
                            case 'contains': return normalizedItemVal.includes(normalizedFilterVal);
                            case 'not_contains': return !normalizedItemVal.includes(normalizedFilterVal);
                            case 'starts_with': return normalizedItemVal.startsWith(normalizedFilterVal);
                            case 'ends_with': return normalizedItemVal.endsWith(normalizedFilterVal);
                            default: return true;
                        }
                    } else {
                        const val = filter.value.toLowerCase();
                        const itemVal = String(item[filter.field] || '').toLowerCase();

                        switch (filter.operator) {
                            case 'equals': return itemVal === val;
                            case 'not_equal': return itemVal !== val;
                            case 'contains': return itemVal.includes(val);
                            case 'not_contains': return !itemVal.includes(val);
                            case 'starts_with': return itemVal.startsWith(val);
                            case 'ends_with': return itemVal.endsWith(val);
                            default: return true;
                        }
                    }
                });

                if (logic === 'ALL') {
                    return matches.every(m => m);
                } else {
                    return matches.some(m => m);
                }
            });
        }
        
        // --- Apply Sorting ---
        if (sorting && sorting.length > 0) {
            const { id, desc } = sorting[0];
            data = [...data].sort((a, b) => {
                let aVal = a[id];
                let bVal = b[id];

                // Special handling for dates
                if (['CreatedDate', 'DeletedOn', 'EstimatedClosureDate'].includes(id)) {
                    const aDate = getValidDate(aVal) || new Date(0);
                    const bDate = getValidDate(bVal) || new Date(0);
                    return desc ? bDate.getTime() - aDate.getTime() : aDate.getTime() - bDate.getTime();
                }

                // Special handling for numbers (Amount)
                if (id === 'Amount') {
                    const aNum = parseFloat(aVal || 0);
                    const bNum = parseFloat(bVal || 0);
                    return desc ? bNum - aNum : aNum - bNum;
                }

                // Default string sorting
                aVal = String(aVal || '').toLowerCase();
                bVal = String(bVal || '').toLowerCase();
                if (aVal < bVal) return desc ? 1 : -1;
                if (aVal > bVal) return desc ? -1 : 1;
                return 0;
            });
        }

        return data;
    }, [historyData, searchTerm, advanceFilterData, statusFilters, sorting]);

    // --- Pagination Logic ---
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const filteredCustomSearch = useMemo(() => {
        const options = [{ CriteriaId: -1, CriteriaName: "All Leads" }, ...customSearchOptions];
        if (!customSearchDropdownSearch) return options;
        return options.filter(opt =>
            (opt.CriteriaName || '').toLowerCase().includes(customSearchDropdownSearch.toLowerCase())
        );
    }, [customSearchOptions, customSearchDropdownSearch]);

    // --- Stats Calculation ---
    const stats = useMemo(() => {
        const total = historyData.length;
        const deleted = historyData.filter(item => String(item.Isdeleted) === "1").length;
        const active = total - deleted;
        const totalValue = historyData.reduce((sum, item) => sum + parseFloat(item.Amount || '0'), 0);
        
        return {
            total: total,
            active: active,
            deleted: deleted,
            totalValue: totalValue
        };
    }, [historyData]);

    const customCards = [
        {
            title: stats.total,
            subtitle: 'Total History',
            icon: 'ri:history-line',
            color: 'primary',
            key: 'total'
        },
        {
            title: stats.active,
            subtitle: 'Active Activities',
            icon: 'ri:checkbox-circle-line',
            color: 'success',
            key: 'active'
        },
        {
            title: stats.deleted,
            subtitle: 'Deleted Items',
            icon: 'ri:delete-bin-line',
            color: 'error',
            key: 'deleted'
        },
        {
            title: new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(stats.totalValue),
            subtitle: 'Opportunity Value',
            icon: 'ri:money-dollar-circle-line',
            color: 'warning',
            key: 'value'
        }
    ];

    // --- Column Setup ---
    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor('PipeLineName', {
            header: 'PIPELINE',
            cell: ({ row }) => (
                <div className="flex flex-col gap-2">
                    <span className={`pipeline-name-badge ${getPipelineBadgeClass(row.original.PipeLineName)}`}>
                        {row.original.PipeLineName || 'N/A'}
                    </span>
                    <div className="flex items-center gap-2 px-0.5">
                         <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider">Amt:</span>
                         <span className="text-[14px] font-bold text-slate-700">₹{parseFloat(row.original.Amount || '0').toLocaleString()}</span>
                    </div>
                </div>
            )
        }),
        columnHelper.accessor('PipeLineStage', {
            header: 'PIPELINE DETAILS',
            cell: ({ row }) => (
                <div className="flex items-center gap-4">
                    {renderProgressCircle(parseInt(row.original.Probability || '0'))}
                    <div className="flex flex-col gap-1">
                        <div className="stage-badge-container">
                            <span className={`stage-badge ${getProbabilityBadgeClass(row.original.Probability)}`}>
                                {row.original.PipeLineStage || 'N/A'}
                            </span>
                        </div>
                        <span className="text-[14px] text-gray-400 line-clamp-1">Remarks: {row.original.Remarks || 'N/A'}</span>
                    </div>
                </div>
            )
        }),
        columnHelper.accessor('EstimatedClosureDate', {
            header: 'CLOSURE DATE',
            cell: ({ row }) => (
                <div className="flex items-center gap-2 text-slate-600">
                    <Calendar size={14} className="text-slate-400" />
                    <span className="text-[14px] font-medium">{formatDate(row.original.EstimatedClosureDate)}</span>
                </div>
            )
        }),
        columnHelper.accessor('CreatedDate', {
            header: 'CREATED DETAILS',
            cell: ({ row }) => (
                <div className="flex flex-col gap-1">
                    <span className="text-[14px] font-medium text-gray-700">Created : {formatDate(row.original.CreatedDate)}</span>
                    {String(row.original.Isdeleted) === "1" && (
                        <span className="text-[11px] text-red-500 font-bold mt-1">Deleted On : {formatDate(row.original.DeletedOn)}</span>
                    )}
                </div>
            )
        }),
        columnHelper.accessor('PersonName', {
            header: 'LEAD NAME',
            cell: ({ row }) => {
                const avatarStyle = getAvatarStyle(row.original.PersonName);
                return (
                    <div className="flex items-center gap-3 cursor-pointer group" onClick={() => {
                        const leadId = row.original.LeadId || row.original.LeadID || row.original.PID || row.original.EnquiryId;
                        if (leadId) setViewingLeadId(leadId);
                    }}>
                        <div 
                            className="lead-avatar" 
                            style={{ 
                                backgroundColor: avatarStyle.bg, 
                                color: avatarStyle.text,
                                borderColor: avatarStyle.border,
                                borderStyle: 'solid',
                                borderWidth: '1px'
                            }}
                        >
                            {getInitials(row.original.PersonName)}
                        </div>
                        <span className="text-[14px] font-semibold text-blue-600 underline underline-offset-2">
                            {row.original.PersonName || 'N/A'}
                        </span>
                    </div>
                );
            }
        }),
        columnHelper.accessor('FName', {
            header: 'CREATED BY',
            cell: ({ row }) => {
                const fullName = [row.original.FName, row.original.LName].filter(Boolean).join(' ') || 'N/A';
                return (
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center bg-white flex-shrink-0 shadow-sm">
                            <User size={18} className="text-slate-400" />
                        </div>
                        <span className="text-[14px] text-slate-700 font-medium whitespace-nowrap">
                            {fullName}
                        </span>
                    </div>
                );
            }
        }),
    ], []);

    // --- Render Progress Circle ---
    const getProbabilityBadgeClass = (prob) => {
        const p = parseInt(prob || '0');
        if (p >= 100) return 'badge-prob-p5';
        if (p >= 76) return 'badge-prob-p4';
        if (p >= 51) return 'badge-prob-p3';
        if (p >= 26) return 'badge-prob-p2';
        return 'badge-prob-p1';
    };

    const renderProgressCircle = (percentage) => {
        const radius = 20;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (Math.min(100, Math.max(0, percentage)) / 100) * circumference;

        return (
            <div className="progress-circle">
                <svg width="50" height="50">
                    <circle cx="25" cy="25" r={radius} stroke="#e5e7eb" strokeWidth="4" fill="transparent" />
                    <circle
                        cx="25" cy="25" r={radius} stroke="#4f46e5" strokeWidth="4"
                        strokeDasharray={circumference} strokeDashoffset={offset}
                        strokeLinecap="round" fill="transparent"
                    />
                </svg>
                <span className="progress-text">{percentage}%</span>
            </div>
        );
    };

    return (
        <div className="pipeline-history-container">
            {/* --- Top Bar Repositioned ABOVE cards --- */}
            <SubHeader
                pageName="Pipeline History"
                pageCode="PAG10307"
                title="Pipeline History"
                customPadding="0 16px"
                viewMode={viewMode === 'list' ? 'table' : viewMode}
                setViewMode={(v) => setViewMode(v === 'table' ? 'list' : v)}
                showGrid={false}
                showTable={true}
                showCalendar={true}
                showSettings={true}
                showAdvanceFilter={!!historyPerms?.Search}
                advanceFilterFields={advanceFilterFields}
                onAdvanceFilterSubmit={historyPerms?.Search ? handleAdvanceFilterSubmit : null}
                filterCount={filterCount}
                showExport={!!historyPerms?.Export}
                onExportClick={historyPerms?.Export ? handleExport : null}
                centerContent={
                    <div className="subheader-center-group">
                        {historyPerms?.Search !== false && (
                            <div className="subheader-search-wrapper">
                                <Search size={18} className="subheader-search-icon themed-icon" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="subheader-search-input"
                                />
                            </div>
                        )}
                        
                        <div className="custom-search-wrapper" style={{ position: 'relative' }}>
                            <div className="custom-search-trigger-container">
                                <button
                                    className={`custom-search-trigger ${selectedCustomSearch ? 'active selection-made' : ''}`}
                                    onClick={() => setCustomSearchDropdownOpen(!customSearchDropdownOpen)}
                                    title={selectedCustomSearch ? `Criteria: ${selectedCustomSearch.CriteriaName}` : 'Select Custom Search'}
                                >
                                    <span className="trigger-text">
                                        {selectedCustomSearch ? selectedCustomSearch.CriteriaName : 'Select Criteria'}
                                    </span>
                                    <ChevronDown size={14} className={`chevron-icon ${customSearchDropdownOpen ? 'rotated' : ''}`} />
                                </button>
                                
                                {selectedCustomSearch && (
                                    <button 
                                        className="custom-search-clear-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedCustomSearch(null);
                                        }}
                                        title="Clear Selection"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>

                            {customSearchDropdownOpen && (
                                <div className="custom-search-dropdown-list">
                                    <div className="dropdown-search-header">
                                        <Search size={14} />
                                        <input
                                            type="text"
                                            placeholder="Search criteria..."
                                            value={customSearchDropdownSearch}
                                            onChange={(e) => setCustomSearchDropdownSearch(e.target.value)}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="dropdown-items">
                                        {filteredCustomSearch.map((opt) => (
                                            <div
                                                key={opt.CriteriaId || opt.ID}
                                                className={`dropdown-item ${selectedCustomSearch?.CriteriaId === opt.CriteriaId ? 'active' : ''}`}
                                                onClick={() => {
                                                    setSelectedCustomSearch(opt.CriteriaId === -1 ? null : opt);
                                                    setCustomSearchDropdownOpen(false);
                                                    setCustomSearchDropdownSearch('');
                                                }}
                                            >
                                                {opt.CriteriaName}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                }
            >
                <div className="date-range-picker-container">
                    <div className="date-range-trigger" onClick={() => setDatePickerOpen(!datePickerOpen)}>
                        <span className="date-text">
                            {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
                        </span>
                        <div className="calendar-icon-wrapper">
                            <Calendar size={18} />
                            <ChevronDown size={14} className="ml-1" />
                        </div>
                    </div>

                    {datePickerOpen && (
                        <div className="date-range-dropdown">
                            <div className="range-options-list">
                                {['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'This Month', 'Last Month', 'Last Year', 'Custom Range'].map((type) => (
                                    <div
                                        key={type}
                                        className={`range-option ${activeRangeLabel === type ? 'active' : ''}`}
                                        onClick={() => handleRangeSelect(type)}
                                    >
                                        {type}
                                    </div>
                                ))}
                            </div>

                            {activeRangeLabel === 'Custom Range' && (
                                <div className="custom-range-inputs">
                                    <div className="input-group">
                                        <label>From</label>
                                        <input
                                            type="date"
                                            value={tempCustomRange.from}
                                            onChange={(e) => handleCustomDateChange('from', e.target.value)}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>To</label>
                                        <input
                                            type="date"
                                            value={tempCustomRange.to}
                                            onChange={(e) => handleCustomDateChange('to', e.target.value)}
                                        />
                                    </div>
                                    <div className="custom-range-actions">
                                        <button className="apply-btn" onClick={applyCustomRange}>Apply</button>
                                        <button className="cancel-btn" onClick={() => setDatePickerOpen(false)}>Close</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </SubHeader>

            <PremiumModuleWrapper
                moduleName="Pipeline History"
                stats={stats}
                onStatusClick={handleStatusFilter}
                customCards={customCards}
                px="16px"
                data={paginatedData}
                columns={columns}
                totalRecords={filteredData.length}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                globalFilter={searchTerm}
                onGlobalFilterChange={setSearchTerm}
                showTable={viewMode !== 'calendar' && filteredData.length > 0}
                showCards={viewMode !== 'calendar'}
                sorting={sorting}
                onSortingChange={setSorting}
                activeFilters={statusFilters}
            >
                {(!loading && filteredData.length === 0) && (
                    <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '20px', padding: '40px', textAlign: 'center' }}>
                        <img src="/nodata.gif" alt="No record Found" style={{ width: '350px', marginBottom: '16px', opacity: 0.8 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            No record Found.
                        </Typography>
                        <Typography sx={{ color: '#64748b', maxWidth: '400px' }}>
                            We couldn't find any records matching your current filters or search term.
                        </Typography>
                    </Box>
                )}
                {viewMode === 'calendar' && filteredData.length > 0 && (
                    <div className="ph-calendar-wrapper">
                        <DynamicCalendar 
                            events={mappedEvents}
                            initialView="month"
                            eventRenderer={renderCalendarEvent}
                            onDateChange={(date, view) => {
                                setCalendarView(view);
                                // Automatically fetch data for the shifted calendar period
                                let from, to;
                                if (view === 'year') {
                                    const first = new Date(date.getFullYear(), 0, 1);
                                    const last = new Date(date.getFullYear(), 11, 31);
                                    from = formatDate(first);
                                    to = formatDate(last);
                                } else if (view === 'month') {
                                    const first = new Date(date.getFullYear(), date.getMonth(), 1);
                                    const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                                    from = formatDate(first);
                                    to = formatDate(last);
                                } else {
                                    const d = new Date(date);
                                    const first = new Date(d.setDate(d.getDate() - d.getDay()));
                                    const last = new Date(d.setDate(d.getDate() + 6));
                                    from = formatDate(first);
                                    to = formatDate(last);
                                }
                                if (dateRange.from !== from || dateRange.to !== to) {
                                    setDateRange({ from, to, label: 'Custom' });
                                }
                            }}
                            statusConfig={{
                                won: '#10b981',
                                hot: '#3b82f6',
                                warm: '#6366f1',
                                cool: '#f59e0b',
                                lost: '#ef4444'
                            }}
                            entityLabel="pipelines"
                            headerLegend={
                                <div className="ph-calendar-legend">
                                    <div className="legend-item"><span className="dot" style={{ backgroundColor: '#10b981' }}></span> 100% Prob.</div>
                                    <div className="legend-item"><span className="dot" style={{ backgroundColor: '#3b82f6' }}></span> 76-99%</div>
                                    <div className="legend-item"><span className="dot" style={{ backgroundColor: '#6366f1' }}></span> 51-75%</div>
                                    <div className="legend-item"><span className="dot" style={{ backgroundColor: '#f59e0b' }}></span> 26-50%</div>
                                    <div className="legend-item"><span className="dot" style={{ backgroundColor: '#ef4444' }}></span> 0-25%</div>
                                </div>
                            }
                        />
                    </div>
                )}
            </PremiumModuleWrapper>


            {/* Centralized Lead Details Overlay */}
            {viewingLeadId && (
                <div className="pipeline-lead-details-overlay-premium" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 10000, background: '#fff'
                }}>
                    <CommonLeadDetails
                        leadId={viewingLeadId}
                        onBack={() => setViewingLeadId(null)}
                        module="pipeline_history"
                    />
                </div>
            )}

            {/* Edit Deal Overlay */}
            {editingDeal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 10001, background: '#fff', overflowY: 'auto'
                }}>
                    <PipelineDealActionsOperation
                        deal={editingDeal}
                        mode="edit"
                        onBack={() => setEditingDeal(null)}
                        onUpdate={() => {
                            setEditingDeal(null);
                            fetchHistory();
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default PipelineHistory;
